import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import { WrappedStoryStateProvider } from 'contexts/Wrapped'
import WrappedLoadingState from './WrappedLoadingState'
import WrappedErrorState from './WrappedErrorState'
import WrappedStoryContent from './WrappedStoryContent'
import useWrappedData from '../hooks/useWrappedData'

const FullPageContainer = styled.div`
  position: fixed;
  inset: 0;
  height: 100vh;
  height: 100dvh;
  z-index: ${zIndex.menuInModal};
  background-color: ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
`

const LOADING_FADE_DURATION_MS = 400

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  opacity: 0;
  transition: opacity ${LOADING_FADE_DURATION_MS}ms ease-out;
  pointer-events: none;

  &.is-visible {
    opacity: 1;
    pointer-events: auto;
  }
`

interface Props {
  uuid?: string
  onClose?: () => void
}

function WrappedStoryMobile({ uuid, onClose }: Props) {
  const { hasError, error, state, dispatch, handleRetry, hasContent, showLoadingOverlay } = useWrappedData({ uuid })

  // Track whether loading overlay should be mounted (stays mounted during fade-out)
  const [isOverlayMounted, setIsOverlayMounted] = useState(true)
  // Track whether loading overlay should be visible (controls opacity)
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)

  useEffect(() => {
    if (showLoadingOverlay) {
      // Show overlay: mount and make visible
      setIsOverlayMounted(true)
      setIsOverlayVisible(true)
    } else {
      // Hide overlay: start fade-out, then unmount after animation
      setIsOverlayVisible(false)
      const timeoutId = setTimeout(() => {
        setIsOverlayMounted(false)
      }, LOADING_FADE_DURATION_MS)
      return () => clearTimeout(timeoutId)
    }
  }, [showLoadingOverlay])

  return (
    <FullPageContainer className="is-full-page">
      {hasError && <WrappedErrorState error={error ?? undefined} onRetry={handleRetry} onClose={onClose} />}
      {!hasError && hasContent && (
        <WrappedStoryStateProvider state={state} dispatch={dispatch}>
          <WrappedStoryContent mode="fullPage" onClose={onClose} />
        </WrappedStoryStateProvider>
      )}
      {isOverlayMounted && (
        <LoadingOverlay className={clsx({ 'is-visible': isOverlayVisible })}>
          <WrappedLoadingState />
        </LoadingOverlay>
      )}
    </FullPageContainer>
  )
}

export default WrappedStoryMobile
